<template>
  <dashboard-layout title="Content Feedback">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search reason / card / user…" />
      <div class="order-box">
        <select v-model="sortOrder">
          <option value="desc">Newest</option>
          <option value="asc">Oldest</option>
        </select>
      </div>
      <span v-if="lastLoaded" class="last-loaded">Last scan: {{ formatDate(lastLoaded) }}</span>
      <button class="refresh-btn" @click="loadData" :disabled="loading">{{ items.length ? 'Rescan' : 'Scan & load' }}</button>
    </template>

    <div v-if="loading" class="scan-loading">
      <loading-state label="" />
      <div class="progress-wrap">
        <div class="progress-head">
          <span>{{ progressLabel }}</span>
          <span v-if="progress.total">{{ progressPct }}%</span>
        </div>
        <div class="progress-track">
          <div class="progress-fill" :class="{ indeterminate: !progress.total }" :style="progress.total ? { width: progressPct + '%' } : {}"></div>
        </div>
        <div class="progress-sub">
          {{ progress.processed.toLocaleString('en-US') }}<span v-if="progress.total"> / {{ progress.total.toLocaleString('en-US') }}</span> docs scanned · {{ progress.found }} reactions found
        </div>
      </div>
    </div>

    <empty-state v-else-if="!items.length" title="No data loaded yet.">
      This page scans all <b>users</b> and <b>compatibility</b> documents — an expensive operation, so it does not run automatically.<br>
      <button class="scan-btn" @click="loadData">Scan &amp; load</button>
    </empty-state>

    <template v-else>
      <div v-if="cacheExpired" class="stale-banner">
        ⚠ This data is older than 2 hours (cache expired). Click "Rescan" for fresh results.
      </div>

      <!-- Summary cards: overall + per surface -->
      <stat-grid>
        <stat-card
          v-for="card in summaryCards"
          :key="card.label"
          :label="card.label"
          :accent="card.label === 'All'"
        >
          {{ card.total }}
          <template #sub>
            <div class="summary-breakdown">
              <span class="up">👍 {{ card.up }}</span>
              <span class="down">👎 {{ card.down }}</span>
              <span class="ratio" v-if="card.total">{{ card.ratio }}% liked</span>
            </div>
            <div class="ratio-bar" v-if="card.total">
              <div class="ratio-fill" :style="{ width: card.ratio + '%' }"></div>
            </div>
          </template>
        </stat-card>
      </stat-grid>

      <p class="note">
        Reactions have no timestamp of their own — dates reflect when the content was generated/updated.
        No data found for the Horoscope surface yet.
      </p>

      <!-- Reason breakdown -->
      <div class="reasons-grid">
        <data-panel title="👍 Like reasons">
          <div v-if="likeReasons.length === 0" class="empty-reasons">No reasons selected</div>
          <div v-for="r in likeReasons" :key="'up-' + r.label" class="reason-row">
            <span class="reason-label">{{ r.label }}</span>
            <div class="reason-bar"><div class="reason-fill up" :style="{ width: r.pct + '%' }"></div></div>
            <span class="reason-count">{{ r.count }}</span>
          </div>
        </data-panel>
        <data-panel title="👎 Dislike reasons">
          <div v-if="dislikeReasons.length === 0" class="empty-reasons">No reasons selected</div>
          <div v-for="r in dislikeReasons" :key="'down-' + r.label" class="reason-row">
            <span class="reason-label">{{ r.label }}</span>
            <div class="reason-bar"><div class="reason-fill down" :style="{ width: r.pct + '%' }"></div></div>
            <span class="reason-count">{{ r.count }}</span>
          </div>
        </data-panel>
      </div>

      <!-- Detail table -->
      <data-panel flush>
        <template #title>Feedback <span class="muted">({{ filteredItems.length.toLocaleString('en-US') }})</span></template>
        <template #actions>
          <filter-chips v-model="surfaceFilter" :options="surfaceOptions" />
          <filter-chips v-model="likeFilter" :options="likeFilterOptions" />
        </template>

        <data-table :columns="columns" :rows="filteredItems" row-key="id">
          <template #cell-reaction="{ row }">
            <status-badge :variant="row.like ? 'success' : 'danger'">{{ row.like ? '👍' : '👎' }}</status-badge>
          </template>
          <template #cell-surface="{ row }">
            <span class="surface-tag">{{ row.surface }}</span>
          </template>
          <template #cell-reasons="{ row }">
            <div class="reason-chips">
              <span v-if="row.reasons.length === 0" class="muted">—</span>
              <span v-for="r in row.reasons" :key="r" class="reason-chip">{{ r }}</span>
            </div>
          </template>
          <template #cell-card="{ row }">
            <span class="card-cell">{{ row.cardTitle }}</span>
          </template>
          <template #cell-user="{ row }">
            <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-date="{ row }">
            {{ formatDate(row.timestampMs) }}
          </template>
          <template #empty>No feedback found</template>
        </data-table>
      </data-panel>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseContentFeedbackService } from '../services/firebaseContentFeedbackService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const columns = [
  { key: 'reaction', label: 'Reaction', width: '90px' },
  { key: 'surface', label: 'Surface', width: '150px' },
  { key: 'reasons', label: 'Reasons' },
  { key: 'card', label: 'Card' },
  { key: 'user', label: 'User', width: '120px' },
  { key: 'date', label: 'Content date', width: '150px' }
]

const likeFilterOptions = [
  { value: 'all', label: 'All' },
  { value: 'up', label: '👍 Likes' },
  { value: 'down', label: '👎 Dislikes' }
]

export default {
  name: 'ContentFeedbackDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, EmptyState, StatusBadge
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseContentFeedbackService()

    const CACHE_KEY = 'contentFeedbackCache_v1'
    const CACHE_TTL = 2 * 60 * 60 * 1000 // ~2 saat

    const items = ref([])
    const loading = ref(false)
    const lastLoaded = ref(null)
    const cacheExpired = ref(false)
    const progress = ref({ phase: 'users', processed: 0, total: 0, found: 0 })
    const searchQuery = ref('')
    const sortOrder = ref('desc')
    const surfaceFilter = ref('All')
    const likeFilter = ref('all')

    const loadFromCache = () => {
      try {
        const raw = localStorage.getItem(CACHE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        items.value = parsed.items || []
        lastLoaded.value = parsed.lastLoaded || null
        cacheExpired.value = !lastLoaded.value || (Date.now() - lastLoaded.value) > CACHE_TTL
      } catch (e) {
        console.error('cache read failed', e)
      }
    }

    const loadData = async () => {
      loading.value = true
      progress.value = { phase: 'users', processed: 0, total: 0, found: 0 }
      try {
        items.value = await service.getAllFeedback(p => { progress.value = p })
        lastLoaded.value = Date.now()
        cacheExpired.value = false
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ items: items.value, lastLoaded: lastLoaded.value }))
        } catch (e) {
          console.error('cache write failed', e)
        }
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const surfaceOptions = computed(() => {
      const set = new Set(items.value.map(i => i.surface))
      return ['All', ...Array.from(set).sort()]
    })

    const buildStat = list => {
      const up = list.filter(i => i.like).length
      const down = list.length - up
      return { total: list.length, up, down, ratio: list.length ? Math.round((up / list.length) * 100) : 0 }
    }

    const summaryCards = computed(() => {
      const cards = [{ label: 'All', ...buildStat(items.value) }]
      const surfaces = Array.from(new Set(items.value.map(i => i.surface))).sort()
      surfaces.forEach(s => {
        cards.push({ label: s, ...buildStat(items.value.filter(i => i.surface === s)) })
      })
      return cards
    })

    const reasonBreakdown = isLike => {
      const counts = {}
      items.value.filter(i => i.like === isLike).forEach(i => {
        i.reasons.forEach(r => { counts[r] = (counts[r] || 0) + 1 })
      })
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1])
      const max = entries.length ? entries[0][1] : 1
      return entries.map(([label, count]) => ({ label, count, pct: Math.round((count / max) * 100) }))
    }
    const likeReasons = computed(() => reasonBreakdown(true))
    const dislikeReasons = computed(() => reasonBreakdown(false))

    const filteredItems = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      let data = items.value.filter(i => {
        if (surfaceFilter.value !== 'All' && i.surface !== surfaceFilter.value) return false
        if (likeFilter.value === 'up' && !i.like) return false
        if (likeFilter.value === 'down' && i.like) return false
        if (q) {
          const hay = `${i.cardTitle} ${i.userId} ${i.reasons.join(' ')}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      data = [...data].sort((a, b) => {
        const diff = (a.timestampMs || 0) - (b.timestampMs || 0)
        return sortOrder.value === 'asc' ? diff : -diff
      })
      return data
    })

    const progressPct = computed(() => {
      const p = progress.value
      return p.total ? Math.min(100, Math.round((p.processed / p.total) * 100)) : 0
    })
    const progressLabel = computed(() => {
      switch (progress.value.phase) {
        case 'users': return 'Scanning birth charts…'
        case 'compatibility': return 'Scanning compatibility…'
        default: return 'Finishing up…'
      }
    })

    const truncate = (text, max) => {
      if (!text) return '-'
      return text.length > max ? text.substring(0, max) + '...' : text
    }

    const formatDate = ms => {
      if (!ms) return '-'
      return new Date(ms).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }

    const openUser = (uid, event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(`/user/${uid}`, '_blank')
      } else {
        router.push(`/user/${uid}`)
      }
    }

    onMounted(loadFromCache)

    return {
      items, loading, lastLoaded, cacheExpired, progress, progressPct, progressLabel,
      searchQuery, sortOrder, surfaceFilter, likeFilter,
      surfaceOptions, summaryCards, likeReasons, dislikeReasons, filteredItems,
      loadData, truncate, formatDate, openUser,
      columns, likeFilterOptions
    }
  }
}
</script>

<style scoped>
/* Header controls */
.order-box select,
.refresh-btn {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
}
.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}
.refresh-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.last-loaded {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Empty / load prompt */
.scan-btn {
  display: inline-block;
  margin-top: 12px;
  padding: 10px 22px;
  background: var(--primary-color);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}
.scan-btn:hover { opacity: 0.92; }

/* Stale cache banner */
.stale-banner {
  background: rgba(248, 191, 113, 0.12);
  border: 1px solid rgba(248, 191, 113, 0.35);
  color: #f8bf71;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
}

/* Summary card sub content */
.summary-breakdown {
  display: flex;
  gap: 12px;
  font-size: 13px;
  align-items: center;
}
.summary-breakdown .up { color: #4ade80; }
.summary-breakdown .down { color: #f87171; }
.summary-breakdown .ratio { color: var(--text-secondary); margin-left: auto; }
.ratio-bar {
  margin-top: 10px;
  height: 6px;
  background: rgba(248, 113, 113, 0.3);
  border-radius: 3px;
  overflow: hidden;
}
.ratio-fill {
  height: 100%;
  background: #4ade80;
  border-radius: 3px;
}
.note {
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary);
  opacity: 0.8;
}

/* Reason breakdown */
.reasons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
}
.reason-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.reason-label {
  font-size: 12.5px;
  color: var(--text-primary);
  width: 150px;
  flex-shrink: 0;
}
.reason-bar {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: hidden;
}
.reason-fill {
  height: 100%;
  border-radius: 4px;
}
.reason-fill.up { background: #4ade80; }
.reason-fill.down { background: #f87171; }
.reason-count {
  font-size: 12px;
  color: var(--text-secondary);
  width: 24px;
  text-align: right;
}
.empty-reasons {
  font-size: 12.5px;
  color: var(--text-secondary);
}

/* Table cells */
.surface-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(108, 99, 255, 0.15);
  border: 1px solid rgba(108, 99, 255, 0.3);
  color: var(--text-primary);
  white-space: nowrap;
}
.reason-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 340px;
}
.reason-chip {
  font-size: 11.5px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
  white-space: nowrap;
}
.card-cell {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.user-link {
  color: var(--primary-color);
  text-decoration: none;
}
.user-link:hover { text-decoration: underline; }
.muted { color: var(--text-secondary); }

/* Loading / scan progress */
.scan-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.progress-wrap {
  width: 100%;
  max-width: 440px;
}
.progress-head {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 8px;
}
.progress-track {
  height: 10px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 5px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  border-radius: 5px;
  transition: width 0.25s ease;
}
.progress-fill.indeterminate {
  width: 35%;
  animation: indeterminate 1.1s ease-in-out infinite;
}
@keyframes indeterminate {
  0% { margin-left: -35%; }
  100% { margin-left: 100%; }
}
.progress-sub {
  margin-top: 10px;
  font-size: 12.5px;
  color: var(--text-secondary);
  text-align: center;
}
</style>
